import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SubscriptionPaymentResultDto } from '@core/models/api/subscription.model';
import { SubscriptionService } from '@core/services/subscription.service';
import { GlobalActions } from '@core/store/global/global.actions';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, of, switchMap } from 'rxjs';

/**
 * Validates the E-Posta redirect return URL before any page UI is shown.
 * On success, stays on /pricing/:SessionId/success/:SuccessCode; on failure, to the 404 page.
 */
export const epostaRedirectReturnGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const subscriptionService = inject(SubscriptionService);
  const store = inject(Store);
  const translate = inject(TranslateService);
  const platformId = inject(PLATFORM_ID);

  const sessionId = route.paramMap.get('SessionId')?.trim();
  const successCode = route.paramMap.get('SuccessCode')?.trim();

  if (!sessionId || !successCode) {
    return router.createUrlTree(['/pricing/return-not-found']);
  }

  // tokenInterceptor returns EMPTY on the server. The router then takes first() of this
  // guard result and throws EmptyError ("no elements in sequence"). Confirm only in the browser.
  if (!isPlatformBrowser(platformId)) {
    return false;
  }

  store.dispatch(GlobalActions.setLoading(true));

  return subscriptionService.confirmEpostaRedirectPayment(successCode, sessionId).pipe(
    switchMap((result: SubscriptionPaymentResultDto) => {
      const alreadyConfirmed = result?.alreadyConfirmed === true;
      const messageKey = alreadyConfirmed
        ? 'PRICING.EPOSTA_PAYMENT_ALREADY_CONFIRMED'
        : 'PRICING.EPOSTA_PAYMENT_CONFIRMED';

      return translate.get(messageKey).pipe(
        map((translated: string) => {
          store.dispatch(
            GlobalActions.setMessage({
              infoMessage: {
                message: translated === messageKey
                  ? alreadyConfirmed
                    ? 'A payment with this success code is already confirmed'
                    : 'Pagesa u konfirmua. Gjendja e abonimit është përditësuar.'
                  : translated,
                type: alreadyConfirmed ? 'warning' : 'success',
              },
            })
          );
          return true;
        })
      );
    }),
    catchError(() => of(router.createUrlTree(['/pricing/return-not-found']))),
    finalize(() => store.dispatch(GlobalActions.setLoading(false)))
  );
};
